/**
 * The one error presentation the whole app uses (owner, 2026-09-23: "اي ايرور يطلع بشيك ونوضح سبب الايرور بشكل شيك").
 *
 * Anatomy, top to bottom:
 *   - header      what failed, in plain Arabic, with the danger mark
 *   - reason      why it failed, in a sentence an operator understands (never a bare "حصل خطأ")
 *   - hint        what to do about it, when there is something to do
 *   - details     a labelled grid about the entity the error is about (shipment, runner…) — empty fields drop out
 *   - actions     نسخ التفاصيل, and اعادة المحاولة when a retry can plausibly work
 *   - technical   HTTP code, raw message and the reference, collapsed behind "تفاصيل فنية" for support
 *
 * Rendered inside a sonner toast by `notify.error`, and reusable inline (the assign screens show the same card
 * under a shipment that cannot be assigned).
 */
import { apiUrl } from '@/api/client'
import { useState } from 'react'
import { AlertOctagon, ArrowCounterClockwise, Check, ChevronDown, Copy, X } from '@/design-system/icons'
import { PaperPlaneTilt } from '@phosphor-icons/react'
import { Badge, Button, Icon, cn } from './primitives'

/** One labelled fact about the thing that failed. Rendered only when `value` has something in it. */
export interface ErrorDetail {
  label: string
  value: string | number | null | undefined
  /** Codes and ids read better monospaced. */
  mono?: boolean
  /** Draws attention — the blocking status, for instance. */
  tone?: 'danger' | 'warning' | 'neutral'
}

export interface ErrorCardProps {
  title: string
  reason: string
  hint?: string
  /** Facts about the entity: AWB, current status, reason, the runner already holding it, notes… */
  details?: ErrorDetail[]
  /** HTTP code, raw upstream text, request reference. */
  technical?: string[]
  onRetry?: () => void
  onDismiss?: () => void
  /** Inline under a form/row rather than inside a toast — slightly quieter chrome. */
  inline?: boolean
  className?: string
}

const shown = (d: ErrorDetail) => d.value !== null && d.value !== undefined && String(d.value).trim() !== '' && String(d.value) !== 'False'

export function ErrorCard({ title, reason, hint, details = [], technical = [], onRetry, onDismiss, inline, className }: ErrorCardProps) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [sent, setSent] = useState<'idle' | 'sending' | 'done' | 'failed'>('idle')
  const facts = details.filter(shown)

  /*
   * What the employee copies is the WHOLE thing (owner, 2026-09-27: "يقدر يعمله نسخ كامل العملية اللي عمل فيها وكود
   * الشحنة وتفاصيل الايرور وريكوست وريسبونس"): the screen they were on, the action, every fact on the card, the
   * technical lines with the reference — and the reference is what lets the gateway attach the request we sent and
   * the answer that came back, which the browser itself never sees.
   */
  const where = typeof window === 'undefined' ? '' : `${window.location.pathname}${window.location.search}`
  const ref = technical.map((t) => /(?:Ref|مرجع)\s+([A-Za-z0-9_-]{6,})/.exec(t)?.[1]).find(Boolean) ?? ''
  const awb = facts.find((d) => /awb|بوليصة|الشحنة/i.test(d.label))?.value
  const copyText = [
    title,
    reason,
    hint ? `المطلوب: ${hint}` : '',
    where ? `الشاشة: ${where}` : '',
    ...facts.map((d) => `${d.label}: ${d.value}`),
    ...technical,
    `الوقت: ${new Date().toLocaleString('ar-EG')}`,
  ].filter(Boolean).join('\n')

  const copy = () => {
    void navigator.clipboard?.writeText(copyText).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      },
      () => undefined,
    )
  }

  /** ارسال الايرور للحل — the report lands in بلاغات الأخطاء with everything above, and comes back with its answer. */
  const report = () => {
    setSent('sending')
    void fetch(apiUrl('/backend/error-reports'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', 'x-aliens-os': '1' },
      body: JSON.stringify({
        page: where,
        action: title,
        awb: awb == null ? '' : String(awb),
        title,
        reason,
        code: technical.map((t) => /\b([A-Z][A-Z_]{3,})\b/.exec(t)?.[1]).find(Boolean) ?? '',
        status: Number(technical.map((t) => /\bHTTP\s+(\d{3})\b/.exec(t)?.[1]).find(Boolean)) || null,
        requestId: ref,
        client: { hint, details: facts.map((d) => ({ label: d.label, value: String(d.value) })), technical, copied: copyText, userAgent: navigator.userAgent },
      }),
    })
      .then((r) => setSent(r.ok ? 'done' : 'failed'))
      .catch(() => setSent('failed'))
  }

  return (
    <div
      role="alert"
      className={cn(
        'animate-pop w-full min-w-0 overflow-hidden rounded-2xl border border-danger/30 bg-surface text-start shadow-lg',
        inline ? 'shadow-xs' : 'sm:w-[26rem]',
        className,
      )}
    >
      <div className="flex items-start gap-3 border-b border-danger/20 bg-danger-soft px-3.5 py-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-xl bg-danger/10">
          <Icon icon={AlertOctagon} size={20} weight="fill" className="text-danger" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-5 text-danger">{title}</p>
          <p className="mt-1 whitespace-pre-line break-words text-sm leading-5 text-fg">{reason}</p>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} aria-label="إغلاق" className="-me-1 -mt-1 shrink-0 rounded-md p-1 text-fg-muted hover:bg-surface-hover hover:text-fg">
            <Icon icon={X} size={15} />
          </button>
        )}
      </div>

      <div className="space-y-3 px-3.5 py-3">
        {hint && <p className="text-xs leading-5 text-fg-muted">{hint}</p>}

        {facts.length > 0 && (
          <dl className="grid gap-x-4 gap-y-2 rounded-xl border border-border bg-bg-subtle px-3 py-2.5 sm:grid-cols-2">
            {facts.map((d, i) => (
              <div key={i} className="min-w-0">
                <dt className="text-2xs text-fg-muted">{d.label}</dt>
                <dd
                  className={cn(
                    'mt-0.5 break-words text-xs font-medium',
                    d.mono && 'font-mono',
                    d.tone === 'danger' && 'text-danger',
                    d.tone === 'warning' && 'text-warning',
                  )}
                >
                  {String(d.value)}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {onRetry && (
            <Button size="sm" variant="secondary" icon={ArrowCounterClockwise} onClick={onRetry}>
              إعادة المحاولة
            </Button>
          )}
          <Button size="sm" variant="ghost" icon={copied ? Check : Copy} onClick={copy}>
            {copied ? 'تم النسخ' : 'نسخ التفاصيل'}
          </Button>
          <Button size="sm" variant="ghost" icon={sent === 'done' ? Check : PaperPlaneTilt} loading={sent === 'sending'} disabled={sent === 'done'} onClick={report}>
            {sent === 'done' ? 'تم الإرسال للمعالجة' : sent === 'failed' ? 'إعادة الإرسال' : 'إرسال الخطأ للمعالجة'}
          </Button>
          {technical.length > 0 && (
            <button
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="ms-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-2xs text-fg-muted hover:bg-surface-hover hover:text-fg"
            >
              تفاصيل فنية
              <Icon icon={ChevronDown} size={11} className={cn('transition-transform', open && 'rotate-180')} />
            </button>
          )}
        </div>

        {open && technical.length > 0 && (
          <div className="animate-fade-in space-y-1 rounded-xl border border-border bg-bg-subtle px-3 py-2">
            {technical.map((line, i) => (
              <p key={i} dir="ltr" className="break-all font-mono text-2xs leading-4 text-fg-muted">
                {line}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/** The same card as a quiet inline block — used under a shipment row that cannot be assigned. */
export function InlineError(props: Omit<ErrorCardProps, 'inline'>) {
  return <ErrorCard {...props} inline />
}

/** A compact one-line version for dense lists: the reason only, with the status as a badge. */
export function ErrorLine({ awb, reason, status }: { awb?: string; reason: string; status?: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-danger/25 bg-danger-soft px-2.5 py-1.5 text-xs">
      <Icon icon={AlertOctagon} size={14} weight="fill" className="shrink-0 text-danger" />
      {awb && <span className="font-mono font-semibold">{awb}</span>}
      <span className="min-w-0 break-words text-fg">{reason}</span>
      {status && (
        <Badge tone="danger" className="ms-auto">
          {status}
        </Badge>
      )}
    </div>
  )
}
